/**
 * Aisle order — editable config (PRD 5.7). The grocery list is grouped in exactly
 * this order, so it should match the way you actually walk the store. Nothing about
 * aisles is hardcoded anywhere in the app; it all lives in D1.
 */
import { useState } from 'react';
import type { Aisle } from '../../shared/types';
import { errMsg, mealsApi } from './mealsApi';
import { ErrorNote, Modal, btnGhost, btnPrimary, btnSecondary, inputCls } from './ui';

export default function AisleEditor({
  aisles,
  onChanged,
  onClose,
}: {
  aisles: Aisle[];
  onChanged: () => Promise<void> | void;
  onClose: () => void;
}) {
  const [order, setOrder] = useState<Aisle[]>(aisles);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Renumber 1..n and persist only the rows whose position actually moved. */
  async function persistOrder(next: Aisle[]) {
    setOrder(next);
    setBusy(true);
    setError(null);
    try {
      const changed = next
        .map((a, i) => ({ aisle: a, sort_order: i + 1 }))
        .filter(({ aisle, sort_order }) => aisle.sort_order !== sort_order);
      await Promise.all(
        changed.map(({ aisle, sort_order }) => mealsApi.updateAisle(aisle.id, { sort_order }))
      );
      setOrder(next.map((a, i) => ({ ...a, sort_order: i + 1 })));
      await onChanged();
    } catch (err) {
      setError(errMsg(err));
      setOrder(aisles);
    } finally {
      setBusy(false);
    }
  }

  function move(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= order.length) return;
    const next = [...order];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    void persistOrder(next);
  }

  async function rename(aisle: Aisle, name: string) {
    const trimmed = name.trim();
    if (!trimmed || trimmed === aisle.name) {
      setOrder((prev) => prev.map((a) => (a.id === aisle.id ? { ...a, name: aisle.name } : a)));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const updated = await mealsApi.updateAisle(aisle.id, { name: trimmed });
      setOrder((prev) => prev.map((a) => (a.id === aisle.id ? updated : a)));
      await onChanged();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove(aisle: Aisle) {
    setBusy(true);
    setError(null);
    try {
      await mealsApi.deleteAisle(aisle.id);
      setOrder((prev) => prev.filter((a) => a.id !== aisle.id));
      await onChanged();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  }

  async function add(e: React.FormEvent) {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    setError(null);
    try {
      const created = await mealsApi.createAisle({ name });
      setOrder((prev) => [...prev, created]);
      setNewName('');
      await onChanged();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Aisle order"
      onClose={onClose}
      footer={
        <button type="button" className={`${btnPrimary} w-full`} onClick={onClose}>
          Done
        </button>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-ink-soft">
          The grocery list is grouped in this order. Put them in the order you walk the store.
        </p>

        {error && <ErrorNote message={error} />}

        {order.length === 0 ? (
          <p className="rounded-xl border border-dashed border-border p-4 text-center text-sm text-ink-soft">
            No aisles yet. Add the first section of your store below.
          </p>
        ) : (
          <ol className="space-y-2">
            {order.map((aisle, i) => (
              <li
                key={aisle.id}
                className="flex items-center gap-1 rounded-xl border border-border bg-canvas p-2"
              >
                <span
                  aria-hidden="true"
                  className="w-6 shrink-0 text-center text-xs font-medium text-ink-faint"
                >
                  {i + 1}
                </span>
                <label className="sr-only" htmlFor={`aisle-${aisle.id}`}>
                  Aisle name
                </label>
                <input
                  id={`aisle-${aisle.id}`}
                  className={`${inputCls} flex-1`}
                  defaultValue={aisle.name}
                  disabled={busy}
                  onBlur={(e) => rename(aisle, e.target.value)}
                />
                <button
                  type="button"
                  className={btnGhost}
                  disabled={busy || i === 0}
                  aria-label={`Move ${aisle.name} up`}
                  onClick={() => move(i, -1)}
                >
                  <span aria-hidden="true">↑</span>
                </button>
                <button
                  type="button"
                  className={btnGhost}
                  disabled={busy || i === order.length - 1}
                  aria-label={`Move ${aisle.name} down`}
                  onClick={() => move(i, 1)}
                >
                  <span aria-hidden="true">↓</span>
                </button>
                <button
                  type="button"
                  className={btnGhost}
                  disabled={busy}
                  aria-label={`Remove ${aisle.name}`}
                  onClick={() => remove(aisle)}
                >
                  <span aria-hidden="true">✕</span>
                </button>
              </li>
            ))}
          </ol>
        )}

        <form onSubmit={add} className="flex gap-2">
          <label className="sr-only" htmlFor="new-aisle">
            New aisle name
          </label>
          <input
            id="new-aisle"
            className={inputCls}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Deli"
            autoComplete="off"
          />
          <button type="submit" className={btnSecondary} disabled={busy || !newName.trim()}>
            Add
          </button>
        </form>

        <p className="text-xs text-ink-faint">
          Removing an aisle keeps it on any ingredient that already uses it — those items just move
          to the bottom of the list.
        </p>
      </div>
    </Modal>
  );
}
